import { Paperclip, ShieldCheck, Star, UserCheck, UserMinus } from '@phosphor-icons/react';
import Modal from '../../../../../shared/components/Modal';
import { CATEGORIES } from '../../../../../shared/constants/categories';
import { PRICE_LEVELS } from '../../../../priceLevel';
import {
  DEFAULT_ADMIN_FILTERS,
  type AdminFilters,
  type AdminTab,
} from '../../../helpers/adminFilters';
import { useState } from 'react';

type AdminFilterModalProps = {
  filters: AdminFilters;
  tab: AdminTab;
  t: any;
  onApply: (filters: AdminFilters) => void;
  onClose: () => void;
};

const SelectButton = ({ selected, children, onClick }: any) => (
  <button type="button" className={selected ? 'selected' : ''} onClick={onClick}>
    {children}
  </button>
);

export default function AdminFilterModal({
  filters,
  tab,
  t,
  onApply,
  onClose,
}: AdminFilterModalProps) {
  const [draft, setDraft] = useState(filters);
  const update = (values: Partial<AdminFilters>) => setDraft((current) => ({ ...current, ...values }));
  const sortOptions = [
    ['name-asc', t.nameAZ],
    ['name-desc', t.nameZA],
    ['created-desc', t.newestFirst],
    ['created-asc', t.oldestFirst],
    ...(tab === 'users' ? [['login-desc', t.recentLogin], ['login-asc', t.oldestLogin]] : []),
    ...(tab === 'history' || tab === 'reviews'
      ? [['rating-desc', t.ratingHigh], ['rating-asc', t.ratingLow]]
      : []),
  ];

  return (
    <Modal onClose={onClose} label={t.sortAndFilter}>
      <div className="admin-filter-modal">
        <div className="modal-head">
          <span className="eyebrow coral">{t.administration}</span>
          <h2>{t.sortAndFilter}</h2>
        </div>

        <div className="filter-section">
          <b>{t.sortBy}</b>
          <div className="favorites-sort-grid">
            {sortOptions.map(([value, label]) => (
              <SelectButton
                key={value}
                selected={draft.sort === value}
                onClick={() => update({ sort: value })}
              >
                {label}
              </SelectButton>
            ))}
          </div>
        </div>

        {tab === 'users' && (
          <div className="filter-section">
            <b>{t.accountType}</b>
            <div className="favorites-type-grid">
              <SelectButton selected={draft.type === 'all'} onClick={() => update({ type: 'all' })}>{t.all}</SelectButton>
              <SelectButton selected={draft.type === 'active'} onClick={() => update({ type: 'active' })}><UserCheck />{t.activeUsers}</SelectButton>
              <SelectButton selected={draft.type === 'banned'} onClick={() => update({ type: 'banned' })}><UserMinus />{t.bannedUsers}</SelectButton>
              <SelectButton selected={draft.type === 'admin'} onClick={() => update({ type: 'admin' })}><ShieldCheck />{t.admins}</SelectButton>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <>
            <div className="filter-section">
              <b>{t.filterByType}</b>
              <div className="favorites-type-grid">
                <SelectButton selected={draft.type === 'all'} onClick={() => update({ type: 'all' })}>{t.all}</SelectButton>
                {Object.entries(CATEGORIES).map(([key, category]) => (
                  <SelectButton key={key} selected={draft.type === key} onClick={() => update({ type: key })}>
                    <category.Icon weight={draft.type === key ? 'fill' : 'regular'} />{t[key]}
                  </SelectButton>
                ))}
              </div>
            </div>
            <div className="filter-section">
              <b>{t.priceRange}</b>
              <div className="admin-price-grid">
                <SelectButton selected={draft.priceLevel === 'all'} onClick={() => update({ priceLevel: 'all' })}>{t.all}</SelectButton>
                {PRICE_LEVELS.map((level) => (
                  <SelectButton key={level} selected={draft.priceLevel === String(level)} onClick={() => update({ priceLevel: String(level) })}>
                    {'$'.repeat(level)} <span>{t[`priceLevel${level}`]}</span>
                  </SelectButton>
                ))}
              </div>
            </div>
            <div className="filter-section admin-rating-filter">
              <div><b>{t.ratingRange}</b><strong><Star weight="fill" />{draft.minRating.toFixed(1)}–{draft.maxRating.toFixed(1)}</strong></div>
              <label><span>{t.minimumRating}</span><input type="range" min="0" max="5" step="0.5" value={draft.minRating} onChange={(event: any) => update({ minRating: Math.min(Number(event.target.value), draft.maxRating) })} /></label>
              <label><span>{t.maximumRating}</span><input type="range" min="0" max="5" step="0.5" value={draft.maxRating} onChange={(event: any) => update({ maxRating: Math.max(Number(event.target.value), draft.minRating) })} /></label>
            </div>
          </>
        )}

        {tab === 'reviews' && (
          <div className="filter-section admin-rating-filter">
            <div><b>{t.ratingRange}</b><strong><Star weight="fill" />{draft.minRating.toFixed(1)}–{draft.maxRating.toFixed(1)}</strong></div>
            <label><span>{t.minimumRating}</span><input type="range" min="0" max="5" step="0.5" value={draft.minRating} onChange={(event: any) => update({ minRating: Math.min(Number(event.target.value), draft.maxRating) })} /></label>
            <label><span>{t.maximumRating}</span><input type="range" min="0" max="5" step="0.5" value={draft.maxRating} onChange={(event: any) => update({ maxRating: Math.max(Number(event.target.value), draft.minRating) })} /></label>
          </div>
        )}

        {tab === 'inquiries' && (
          <div className="filter-section">
            <b>{t.inquiryType}</b>
            <div className="favorites-type-grid">
              <SelectButton selected={draft.type === 'all'} onClick={() => update({ type: 'all' })}>{t.all}</SelectButton>
              <SelectButton selected={draft.type === 'attachments'} onClick={() => update({ type: 'attachments' })}><Paperclip />{t.withAttachments}</SelectButton>
              <SelectButton selected={draft.type === 'no-attachments'} onClick={() => update({ type: 'no-attachments' })}>{t.withoutAttachments}</SelectButton>
            </div>
          </div>
        )}

        <div className="modal-actions split">
          <button type="button" className="secondary" onClick={() => setDraft({ ...DEFAULT_ADMIN_FILTERS[tab] })}>{t.resetFilters}</button>
          <div>
            <button type="button" className="secondary" onClick={onClose}>{t.cancel}</button>
            <button type="button" className="primary" onClick={() => onApply(draft)}>{t.applyFilters}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
